import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import Icon from '../components/Icon';
import { compact } from '../lib/metrics';
import { money, fmtRoas } from './Growth';

export default function PublisherIndex() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.publisherIndex().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout title={t('pubidx.title')} heading={false}>
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  const publishers = data?.publishers || [];

  return (
    <DashboardLayout title={t('pubidx.title')} heading={false}>
      <Link to="/dashboard/growth" style={{ fontSize: 12.5, color: 'var(--text-3)', display: 'inline-flex', gap: 4, alignItems: 'center', marginBottom: 10 }}>
        <Icon name="arrow" size={12} style={{ transform: 'rotate(180deg)' }} /> {t('growth.title')}
      </Link>
      <h1 className="page-title">{t('pubidx.title')}</h1>
      <p className="page-sub">{t('pubidx.sub')}</p>
      <p className="growth-disclaimer" style={{ marginTop: -8, marginBottom: 'var(--sp-4)' }}>
        {t('pubidx.privacy_note', { n: data?.minCustomers ?? 3 })}
      </p>

      {publishers.length === 0 ? (
        <div className="card empty-state">
          <Icon name="bar-chart" size={28} style={{ color: 'var(--text-3)', marginBottom: 10 }} />
          <h3>{t('pubidx.empty_title')}</h3>
          <p>{t('pubidx.empty_desc', { n: data?.minCustomers ?? 3 })}</p>
          {data?.totalPublishersSeen > 0 && (
            <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 10 }}>
              {t('pubidx.empty_progress', { seen: data.totalPublishersSeen, campaigns: data.totalCampaignsSeen })}
            </p>
          )}
          <Link to="/dashboard/growth" className="btn btn-sm" style={{ marginTop: 'var(--sp-4)' }}>{t('growth.add_campaign')}</Link>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="ag-table">
            <thead>
              <tr>
                <th>{t('pubidx.publisher')}</th>
                <th>{t('pubidx.customers')}</th>
                <th>{t('pubidx.campaigns')}</th>
                <th>{t('pubidx.clicks')}</th>
                <th>{t('pubidx.est_regs')}</th>
                <th>{t('pubidx.revenue')}</th>
                <th>{t('pubidx.roas')}</th>
                <th>{t('pubidx.cpr')}</th>
              </tr>
            </thead>
            <tbody>
              {publishers.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td>{t('pubidx.n_servers', { n: p.customers })}</td>
                  <td>{p.campaigns}</td>
                  <td>{compact(p.clicks, lang)}</td>
                  <td>{compact(p.estRegistrations, lang)}</td>
                  <td>{p.revenue != null ? money(p.revenue, lang) : '—'}</td>
                  <td>{p.roas != null ? <span className={p.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(p.roas, lang)}</span> : '—'}</td>
                  <td>{money(p.costPerRegistration, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.pendingCount > 0 && (
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 12 }}>
          {t('pubidx.pending_note', { n: data.pendingCount })}
        </p>
      )}
    </DashboardLayout>
  );
}
